import styled, { css } from "styled-components";
import { getLocale } from "react-i18nify";

import device from "src/styles/device";
import font from "src/styles/font";

import {
  TextField,
  Grid,
  Box,
  Autocomplete,
  createFilterOptions,
  IconButton,
  FormControlLabel,
  InputAdornment,
  Checkbox,
} from "@mui/material";

import Button from "src/components/core/Button";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import useModal from "src/hooks/useModal";
import useDeviceSize from "src/hooks/useDeviceSize";
import { UseUpload } from "src/context/UploadProductContext";
import { convertPrice } from "src/common";

import ModalAddition from "src/modules/quotation-draft/CreateQuoDraft/AdditionModal";
import EditSheet from "src/modules/quotation-draft/CreateQuoDraft/ProductSheet/EditSheet";

const WrapperGrid = styled.div`
  width: 100%;
  /* margin-top: 24px; */
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  border-radius: 24px;
  background-color: var(--white-color);
  /* box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1); */

  ::-webkit-scrollbar {
    width: 5px;
    height: 7px;
    display: none;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--grey-color);
    border-radius: 100px;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--grey-color);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;

    ${({ empty }) =>
      empty
        ? css`
            border-radius: 24px 24px 0 0;
            box-shadow: none;
          `
        : css`
            border-radius: 24px;
            box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
          `}
  }
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey" ? "var(--darkgrey-color)" : "var(--secondary-color)"};
  font-size: ${font.size.xs};
  font-weight: 400;
  margin-bottom: 8px;

  @media ${device.tabletL} {
    font-size: ${font.size.sm};

    ${({ nowrap }) =>
      nowrap
        ? css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `
        : css`
            white-space: pre-line;
            word-break: break-word;
          `}
  }
`;

const TextDetail = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey" ? "var(--darkgrey-color)" : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;

  :last-child {
    margin-bottom: 0;
  }

  ${({ nowrap }) =>
    nowrap
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          white-space: pre-line;
          word-break: break-word;
        `}/* @media ${device.tabletL} {
    font-size: ${font.size.sm};
  } */
`;

const ProductDataGrid = ({
  formErrors,
  formData,
  onChange,
  onAppendElement, // add default product
  onRemoveElement,
  dataProductList,
  initialConfirm,
  setConfirmInfo,
}) => {
  const [deviceWidth] = useDeviceSize();

  const { getModalState, open, close } = useModal();
  const { masterdata } = UseUpload();

  const color_data = masterdata.color_data;

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  const handleOnRemove = (index) => {
    if (formData.product_list.length < 2) {
      onRemoveElement(index);
      onAppendElement();
      onChange("", "", "");
    } else {
      onRemoveElement(index);
    }
  };

  return (
    <>
      <WrapperGrid
        empty={formData?.product_list[0]?.product_choice_id === null}
      >
        <Grid
          container
          sx={{
            m: 0,
            width: "100%",
            minWidth: {
              xs: "670px",
              sm: "710px",
              md: "unset",
            },
          }}
        >
          {/* Header */}
          <Grid
            item
            xs={12}
            sx={{
              px: { lg: 5, xs: 3 },
              py: 2,
              backgroundColor: "var(--darkwhite-color)",
              borderRadius: "24px 24px 0 0",
              fontSize: font.size.sm,
            }}
          >
            <Grid
              container
              columnSpacing={{ xs: 2, lg: 3 }}
              sx={{
                textAlign: "center",
                alignItems: "center",
              }}
            >
              <Grid item xs={4} md={6} sx={{ textAlign: "left" }}>
                สินค้า
              </Grid>

              <Grid item xs={1.9} md={1.3}>
                จำนวน
              </Grid>

              <Grid item xs={1.9} md={1.4}>
                ราคาต่อหน่วย
              </Grid>

              <Grid item xs={1.9} md={1.3}>
                ส่วนลด
              </Grid>

              <Grid item xs={2.3} md={1.6}>
                ราคารวมทั้งหมด
              </Grid>
            </Grid>
          </Grid>

          {formData?.product_list?.length > 0 &&
            formData.product_list?.map((product, index) => (
              <>
                {deviceWidth > 835 ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        px: { lg: 5, xs: 3 },
                        py: 3,
                        fontSize: font.size.sm,
                        borderBottom: "1px solid var(--lightgrey-color)",
                      }}
                      key={`product-desktop-${index}`}
                    >
                      <Grid
                        container
                        columnSpacing={{ xs: 2, lg: 3 }}
                        sx={{
                          fontSize: font.size.md,
                          ".MuiAutocomplete-root": {
                            margin: 0,
                          },
                          ".MuiTextField-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-input": {
                            py: "10px",
                            textAlign: "center",
                          },
                          ".MuiInputBase-inputMultiline": {
                            p: 0,
                            textAlign: "left",
                          },
                          ".MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input":
                            {
                              px: 0,
                            },
                        }}
                      >
                        <Grid item xs={6}>
                          <Autocomplete
                            aria-required={true}
                            id="search-product-select"
                            isOptionEqualToValue={(option, value) =>
                              option._id === value._id
                            }
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                style={{
                                  borderBottom:
                                    "4px solid var(--darkwhite-color)",
                                  ":lastChild": {
                                    borderBottom: "none",
                                  },
                                }}
                                key={`product-quotation-${option._id}`}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    py: 2,
                                    display: "flex",
                                  }}
                                >
                                  <Box sx={{ mr: 1 }}>
                                    {!!option?.group_image
                                      ?.product_display_image === false ? (
                                      <ImageEmpty>
                                        <InsertPhotoIcon
                                          sx={{
                                            color: "rgba(0, 0, 0, 0.54)",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </ImageEmpty>
                                    ) : (
                                      <img
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                          objectPosition: "center",
                                          borderRadius: "16px",
                                          marginRight: "8px",
                                        }}
                                        src={
                                          option?.group_image
                                            ?.product_display_image
                                            ? option?.group_image?.product_display_image.replace(
                                                ".com",
                                                ".com/300x300",
                                              )
                                            : ""
                                        }
                                        alt={
                                          option?.group_image
                                            ?.product_display_image
                                        }
                                      />
                                    )}
                                  </Box>

                                  <Box
                                    sx={{
                                      width: "100%",
                                      maxWidth: "calc(100% - 88px)",
                                    }}
                                  >
                                    <TextOption>
                                      {option?.product?.item_name}
                                    </TextOption>

                                    <TextOption color="grey">
                                      {option?.product?.item_code}
                                    </TextOption>

                                    <Box
                                      sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <img
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          objectFit: "cover",
                                          objectPosition: "center",
                                          borderRadius: "50%",
                                        }}
                                        src={
                                          color_data.find(
                                            (detail) =>
                                              detail._id === option?.color._id,
                                          )?.icon_img || ""
                                        }
                                        alt={
                                          option?.group_image
                                            ?.product_display_image
                                        }
                                      />

                                      <TextOption
                                        color="grey"
                                        sx={{ ml: 1, mb: 0 }}
                                      >
                                        {getLocale() === "th"
                                          ? option?.color?.color_th
                                          : option?.color?.color_en}
                                      </TextOption>
                                    </Box>

                                    <TextOption color="grey">
                                      {option?.size?.quantity === "size"
                                        ? `${option?.size?.width} ${
                                            getLocale() === "th"
                                              ? option?.size?.unit_width
                                                  ?.unit_th
                                              : option?.size?.unit_width
                                                  ?.unit_en
                                          } x ${option?.size?.height} ${
                                            getLocale() === "th"
                                              ? option?.size?.unit_height
                                                  ?.unit_th
                                              : option?.size?.unit_height
                                                  ?.unit_en
                                          } x ${option?.size?.thickness} ${
                                            getLocale() === "th"
                                              ? option?.size?.unit_thickness
                                                  ?.unit_th
                                              : option?.size?.unit_thickness
                                                  ?.unit_en
                                          }`
                                        : option?.size?.quantity === "capacity"
                                          ? `${option?.size?.capacity_size} ${
                                              getLocale() === "th"
                                                ? option?.size?.unit_capacity
                                                    ?.unit_th
                                                : option?.size?.unit_capacity
                                                    ?.unit_en
                                            }`
                                          : getLocale() === "th"
                                            ? "สินค้าสั่งทำพิเศษ"
                                            : "Made by order"}
                                    </TextOption>

                                    <TextOption color="grey">
                                      {`${convertPrice(
                                        option?.group_price?.is_start_price ===
                                          true
                                          ? option?.group_price?.start_price
                                          : option?.group_price.special_price >
                                                0 &&
                                              option?.group_price
                                                .special_price !== null &&
                                              option?.group_price
                                                .special_price !== undefined
                                            ? option?.group_price.special_price
                                            : option?.group_price?.price,
                                      )}/${
                                        getLocale() === "th"
                                          ? option?.group_price?.unit_price
                                              ?.unit_th
                                          : option?.group_price?.unit_price
                                              ?.unit_en
                                      }`}
                                    </TextOption>
                                  </Box>
                                </Box>
                              </li>
                            )}
                            options={dataProductList}
                            value={product.product_choice_id}
                            onChange={(event, newValue) => {
                              onChange(
                                null,
                                `product_list.${index}.product_choice_id`,
                                newValue,
                              );
                            }}
                            getOptionLabel={(option) => {
                              return (
                                option?.product?.item_name +
                                " - " +
                                option?.product?.item_code +
                                (option?.name !== ""
                                  ? ` - (` + option?.name + `)`
                                  : ``)
                              );
                            }}
                            filterOptions={filterOptions}
                            sx={{
                              my: 2,
                              width: "100%",
                              background: "#fff",
                              ".MuiOutlinedInput-root": {
                                paddingLeft: "16px",
                                textAlign: "left",
                              },
                              ".MuiOutlinedInput-input": {
                                textAlign: "left",
                              },
                              // "label[data-shrink=false]": {
                              //   opacity: 0.65,
                              // },
                            }}
                            renderInput={(params) => (
                              <TextField
                                className="TextField-without-border-radius"
                                {...params}
                                placeholder="เพิ่มสินค้า"
                                error={
                                  !!formErrors[
                                    `product_list[${index}].product_choice_id`
                                  ]
                                }
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={1.3}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                            }}
                            value={product?.volume}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(e, `product_list.${index}.volume`);
                            }}
                            error={
                              !!formErrors[`product_list[${index}].volume`]
                            }
                          />
                        </Grid>

                        <Grid item xs={1.4}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.price}
                          />
                        </Grid>

                        <Grid item xs={1.3}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.discount}
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `product_list.${index}.discount`,
                                  e.target.value,
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                onChange(
                                  null,
                                  `product_list.${index}.discount`,
                                  parseFloat(e.target.value) || 0,
                                );
                            }}
                            error={
                              !!formErrors[`product_list[${index}].discount`]
                            }
                          />
                        </Grid>

                        <Grid item xs={1.6}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            sx={{
                              pt: "9px",
                              ".MuiInputAdornment-root": {
                                position: "absolute",
                                left: "14px",
                              },
                            }}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            value={product?.total}
                          />
                        </Grid>

                        <Grid item xs={0.4} sx={{ position: "relative" }}>
                          <IconButton
                            aria-label="delete"
                            disabled={
                              formData?.product_list?.length < 2 &&
                              !product.product_choice_id
                            }
                            onClick={() => {
                              if (!!product.product_choice_id) {
                                setConfirmInfo((info) => ({
                                  ...info,
                                  visible: true,
                                  title:
                                    getLocale() === "th"
                                      ? `คุณต้องการลบสินค้าใช่หรือไม่`
                                      : `คุณต้องการลบสินค้าใช่หรือไม่`,
                                  confirmFunc: () => {
                                    handleOnRemove(index);
                                    setConfirmInfo(initialConfirm);
                                  },
                                }));
                              } else {
                                onRemoveElement(index);
                              }
                            }}
                            sx={{
                              position: "absolute",
                              top: "10px",
                              right: {
                                xs: "-70%",
                                lg: "-40%",
                              },
                              // top: "50%",
                              // transform: "translate(-40%, -65%);",
                              ":disabled": {
                                ".MuiSvgIcon-root": {
                                  color: "var(--darkgrey-color)",
                                },
                              },
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{ color: "var(--primary-color)" }}
                            />
                          </IconButton>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2, textAlign: "left" }}>
                          <FormControlLabel
                            label="เพิ่มรายละเอียดสินค้า"
                            sx={{
                              color: "var(--secondary-color)",
                              fontSize: font.size.sm,
                              fontWeight: 400,
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  ":hover": {
                                    ".MuiSvgIcon-root": {
                                      color: "var(--primary-color)",
                                    },
                                  },
                                  "&.MuiFormControlLabel-label.Mui-disabled": {
                                    color: "var(--secondary-color)",
                                  },
                                }}
                                icon={
                                  <RadioButtonUncheckedIcon
                                    sx={{
                                      color: "var(--lightgrey-color)",
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <RadioButtonCheckedIcon
                                    sx={{ color: "var(--primary-color)" }}
                                  />
                                }
                                checked={
                                  product?.description !== "" ? true : false
                                }
                                disabled={
                                  product?.description !== "" ||
                                  product?.product_choice_id === null
                                    ? true
                                    : false
                                }
                                onChange={(e) => {
                                  open(
                                    `product_list.${index}.description.add-product-descripition-modal`,
                                  );
                                }}
                              />
                            }
                          />
                        </Grid>

                        {product?.description !== "" ? (
                          <>
                            <Grid
                              item
                              xs={12}
                              sx={{ mb: 1, textAlign: "left" }}
                            >
                              <Button
                                variant="text"
                                sx={{
                                  px: 3,
                                  py: 1,
                                  borderRadius: 3,
                                }}
                                onClick={() => {
                                  open(
                                    `product_list.${index}.description.add-product-descripition-modal`,
                                    true,
                                  );
                                }}
                              >
                                แนะนำรายละเอียด
                              </Button>
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                multiline
                                fullWidth
                                sx={{
                                  ".MuiOutlinedInput-root": {
                                    p: 2,
                                  },
                                  ".MuiOutlinedInput-input": {
                                    py: 0,
                                    lineHeight: 1.8,
                                    textAlign: "left",
                                  },
                                }}
                                value={product?.description}
                                onChange={(e) => {
                                  onChange(
                                    e,
                                    `product_list.${index}.description`,
                                  );
                                }}
                                error={
                                  !!formErrors[
                                    `product_list[${index}].description`
                                  ]
                                }
                              />
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : product?.product_choice_id !== null ? (
                  <>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 3,
                        fontSize: font.size.sm,
                        borderBottom: "1px solid var(--lightgrey-color)",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                      key={`product-${index}`}
                    >
                      <Grid
                        container
                        columnSpacing={{ xs: 2, lg: 4 }}
                        sx={{
                          fontSize: font.size.md,
                          ".MuiAutocomplete-root": {
                            margin: 0,
                          },
                          ".MuiTextField-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-root": {
                            borderRadius: "24px",
                          },
                          ".MuiOutlinedInput-input": {
                            py: "10px",
                            fontSize: font.size.sm,
                            textAlign: "center",
                          },
                          ".MuiInputBase-inputMultiline": {
                            p: 0,
                            textAlign: "left",
                          },
                          ".MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input":
                            {
                              px: 0,
                            },
                        }}
                      >
                        <Grid
                          item
                          xs={12}
                          container
                          columnSpacing={2}
                          onClick={() => {
                            open(
                              `product_list.${index}.product_choice_id.edit-product-detail-modal`,
                            );
                          }}
                        >
                          <Grid item xs={4}>
                            <Box sx={{ width: "100%" }}>
                              <TextDetail nowrap>
                                {product?.product_choice_id?.product?.item_name}
                              </TextDetail>

                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <img
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    borderRadius: "50%",
                                  }}
                                  src={
                                    color_data.find(
                                      (detail) =>
                                        detail?._id ===
                                        product?.product_choice_id?.color._id,
                                    )?.icon_img || ""
                                  }
                                  alt={
                                    product?.product_choice_id?.group_image
                                      ?.product_display_image
                                  }
                                />

                                <TextDetail color="grey" sx={{ ml: 1, mb: 0 }}>
                                  {getLocale() === "th"
                                    ? product?.product_choice_id?.color
                                        ?.color_th
                                    : product?.product_choice_id?.color
                                        ?.color_en}
                                </TextDetail>
                              </Box>

                              <TextDetail color="grey">
                                {product?.product_choice_id?.size?.quantity ===
                                "size"
                                  ? `${
                                      product?.product_choice_id?.size?.width
                                    } ${
                                      getLocale() === "th"
                                        ? product?.product_choice_id?.size
                                            ?.unit_width?.unit_th
                                        : product?.product_choice_id?.size
                                            ?.unit_width?.unit_en
                                    } x ${
                                      product?.product_choice_id?.size?.height
                                    } ${
                                      getLocale() === "th"
                                        ? product?.product_choice_id?.size
                                            ?.unit_height?.unit_th
                                        : product?.product_choice_id?.size
                                            ?.unit_height?.unit_en
                                    } x ${
                                      product?.product_choice_id?.size
                                        ?.thickness
                                    } ${
                                      getLocale() === "th"
                                        ? product?.product_choice_id?.size
                                            ?.unit_thickness?.unit_th
                                        : product?.product_choice_id?.size
                                            ?.unit_thickness?.unit_en
                                    }`
                                  : product?.product_choice_id?.size
                                        ?.quantity === "capacity"
                                    ? `${
                                        product?.product_choice_id?.size
                                          ?.capacity_size
                                      } ${
                                        getLocale() === "th"
                                          ? product?.product_choice_id?.size
                                              ?.unit_capacity?.unit_th
                                          : product?.product_choice_id?.size
                                              ?.unit_capacity?.unit_en
                                      }`
                                    : getLocale() === "th"
                                      ? "สินค้าสั่งทำพิเศษ"
                                      : "Made by order"}
                              </TextDetail>
                            </Box>
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              InputProps={{
                                readOnly: true,
                              }}
                              value={product?.volume}
                            />
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.price)}
                            />
                          </Grid>

                          <Grid item xs={1.9}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.discount)}
                            />
                          </Grid>

                          <Grid item xs={2.3}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              sx={{
                                ".MuiInputAdornment-root": {
                                  position: "absolute",
                                  left: "14px",
                                },
                              }}
                              InputProps={{
                                readOnly: true,
                                // startAdornment: (
                                //   <InputAdornment position="start">
                                //     ฿
                                //   </InputAdornment>
                                // ),
                              }}
                              value={convertPrice(product?.total)}
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <FormControlLabel
                            label="เพิ่มรายละเอียดสินค้า"
                            sx={{
                              color: "var(--secondary-color)",
                              fontSize: font.size.sm,
                              fontWeight: 400,
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  ":hover": {
                                    ".MuiSvgIcon-root": {
                                      color: "var(--primary-color)",
                                    },
                                  },
                                }}
                                icon={
                                  <RadioButtonUncheckedIcon
                                    sx={{
                                      color: "var(--lightgrey-color)",
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <RadioButtonCheckedIcon
                                    sx={{ color: "var(--primary-color)" }}
                                  />
                                }
                                checked={
                                  product?.description !== "" ? true : false
                                }
                                disabled={
                                  product?.description !== "" ||
                                  product?.product_choice_id === null
                                    ? true
                                    : false
                                }
                                onChange={(e) => {
                                  open(
                                    `product_list.${index}.description.add-product-descripition-modal`,
                                  );
                                }}
                              />
                            }
                          />
                        </Grid>

                        {product?.description !== "" ? (
                          <>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <TextField
                                variant="outlined"
                                multiline
                                fullWidth
                                sx={{
                                  ".MuiOutlinedInput-root": {
                                    p: 2,
                                  },
                                  ".MuiOutlinedInput-input": {
                                    py: 0,
                                    lineHeight: 1.8,
                                    textAlign: "left",
                                  },
                                }}
                                value={product?.description}
                                inputProps={{
                                  readOnly: true,
                                }}
                                onClick={() => {
                                  open(
                                    `product_list.${index}.description.add-product-descripition-modal`,
                                    true,
                                  );
                                }}
                              />
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : null}

                {/* edit tablet/mobile product */}
                <EditSheet
                  docType="product"
                  quoItemDetail={product}
                  isOpen={
                    getModalState(
                      `product_list.${index}.product_choice_id.edit-product-detail-modal`,
                    )?.isOpen
                  }
                  onClose={() =>
                    close(
                      `product_list.${index}.product_choice_id.edit-product-detail-modal`,
                    )
                  }
                  onSubmit={(val) => {
                    onChange(
                      null,
                      `product_list.${index}.edit.product_choice_id`,
                      val,
                    );
                  }}
                  onRemoveElement={() => {
                    setConfirmInfo((info) => ({
                      ...info,
                      visible: true,
                      title:
                        getLocale() === "th"
                          ? `คุณต้องการลบสินค้าใช่หรือไม่`
                          : `คุณต้องการลบสินค้าใช่หรือไม่`,
                      confirmFunc: () => {
                        handleOnRemove(index);
                        close(
                          `product_list.${index}.product_choice_id.edit-product-detail-modal`,
                        );
                        setConfirmInfo(initialConfirm);
                      },
                    }));
                  }}
                  // isRemoveDisabled={formData.product_list?.length <= 1}
                  isRemoveDisabled={false}
                />

                {/* add description */}
                <ModalAddition
                  type="desc"
                  descData={product?.description}
                  docType="product"
                  isOpen={
                    getModalState(
                      `product_list.${index}.description.add-product-descripition-modal`,
                    )?.isOpen
                  }
                  isEdit={
                    getModalState(
                      `product_list.${index}.description.add-product-descripition-modal`,
                    )?.isEdit
                  }
                  onClose={() =>
                    close(
                      `product_list.${index}.description.add-product-descripition-modal`,
                    )
                  }
                  onSubmit={(val) => {
                    onChange(null, `product_list.${index}.description`, val);
                    close(
                      `product_list.${index}.description.add-product-descripition-modal`,
                    );
                  }}
                />
              </>
            ))}

          {/* Add product */}
          {deviceWidth > 835 ? (
            <Grid
              item
              xs={12}
              sx={{
                px: { lg: 5, xs: 3 },
                pt: 3,
                pb: 5,
                fontSize: font.size.sm,
              }}
            >
              <Button
                variant="text"
                startIcon={<AddIcon />}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 3,
                }}
                onClick={onAppendElement}
              >
                Add Product
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </WrapperGrid>
    </>
  );
};

export default ProductDataGrid;
